import React, { useState, useEffect } from 'react';
import { Button } from '@os/components/ui/button';
import { Card } from '@os/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@os/components/ui/dialog';
import { Input } from '@os/components/ui/input';
import { 
  Folder, 
  FileText, 
  ArrowLeft, 
  Home, 
  Plus,
  Trash2,
  Upload
} from 'lucide-react';
import { supabase } from '@os/integrations/supabase/client';
import { useAuth } from '@os/hooks/useAuth';
import { useToast } from '@os/hooks/use-toast';

interface FileItem {
  id: string;
  file_name: string;
  file_path: string;
  parent_path: string | null;
  is_directory: boolean;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
  updated_at: string;
}

interface FileBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect?: (file: FileItem) => void;
  mode?: 'open' | 'save' | 'browse';
  initialPath?: string;
  acceptedTypes?: string[];
}

export const FileBrowser: React.FC<FileBrowserProps> = ({
  isOpen,
  onClose,
  onFileSelect,
  mode = 'open',
  initialPath = 'C:\\',
  acceptedTypes = ['text/html', 'text/plain']
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [isLoading, setIsLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Add a helper to format date+time consistently
  const formatDateTime = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (isOpen && user) {
      loadFiles(currentPath);
    }
  }, [isOpen, currentPath, user]);

  const loadFiles = async (path: string) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_user_files')
        .select('*')
        .eq('user_id', user.id)
        .eq('parent_path', path)
        .order('is_directory', { ascending: false })
        .order('file_name', { ascending: true });

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      toast({
        title: "Error Loading Files",
        description: "Failed to load files from the directory.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = (folderPath: string) => {
    setCurrentPath(folderPath);
  };

  const navigateUp = () => {
    if (currentPath === 'C:\\') return;
    
    const pathParts = currentPath.split('\\');
    pathParts.pop();
    const parentPath = pathParts.join('\\') || 'C:\\';
    setCurrentPath(parentPath);
  };

  const createFolder = async () => {
    if (!user || !newFolderName.trim()) return;

    try {
      const folderPath = `${currentPath}\\${newFolderName}`;
      
      const { error } = await supabase
        .from('os_user_files')
        .insert({
          user_id: user.id,
          file_path: folderPath,
          file_name: newFolderName,
          parent_path: currentPath,
          is_directory: true,
          content_type: null,
          content: null
        });

      if (error) throw error;

      setNewFolderName('');
      setShowNewFolder(false);
      loadFiles(currentPath);
      
      toast({
        title: "Folder Created",
        description: `Created folder: ${newFolderName}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create folder.",
        variant: "destructive",
      });
    }
  };

  const deleteFile = async (file: FileItem) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('os_user_files')
        .delete()
        .eq('id', file.id)
        .eq('user_id', user.id);

      if (error) throw error;

      loadFiles(currentPath);
      
      toast({
        title: "Deleted",
        description: `Deleted: ${file.file_name}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete file.",
        variant: "destructive",
      });
    }
  };

  const handleFileClick = (file: FileItem) => {
    if (file.is_directory) {
      navigateToFolder(file.file_path);
    } else if (onFileSelect) {
      onFileSelect(file);
      onClose();
    }
  };

  const getFileIcon = (file: FileItem) => {
    if (file.is_directory) {
      return <Folder className="w-5 h-5 text-blue-500" />;
    }
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] z-[9999]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Folder className="w-5 h-5" />
            {mode === 'open' ? 'Open File' : mode === 'save' ? 'Save File' : 'Browse Files'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-[60vh]">
          {/* Navigation */}
          <div className="flex items-center gap-2 p-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={navigateUp}
              disabled={currentPath === 'C:\\'}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPath('C:\\')}
            >
              <Home className="w-4 h-4" />
            </Button>
            <div className="flex-1 text-sm text-muted-foreground">
              {currentPath}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewFolder(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              New Folder
            </Button>
          </div>

          {/* New Folder Input */}
          {showNewFolder && (
            <div className="flex items-center gap-2 p-2 border-b bg-muted/50">
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && createFolder()}
              />
              <Button size="sm" onClick={createFolder}>
                Create
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setShowNewFolder(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          {/* File List */}
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-sm text-muted-foreground">Loading...</div>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer group"
                    onClick={() => handleFileClick(file)}
                  >
                    {getFileIcon(file)}
                    <div className="flex-1">
                      <div className="text-sm font-medium">{file.file_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {!file.is_directory && formatFileSize(file.size_bytes)}
                        {file.content_type && ` • ${file.content_type}`}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(file.updated_at)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFile(file);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {files.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Folder className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <div>This folder is empty</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};